/**
 * PM API client — typed functions for the PM system endpoints.
 * Extends the base apiClient from the opsatlas.
 */
import { apiClient } from './index';
import { queryClient } from './queryClient';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface EntityType {
  id: string;
  name: string;
  slug: string;
  icon: string;
  color: string;
  is_system: boolean;
}

export type ProjectType = 'general' | 'software';

export interface Project {
  project_type?: ProjectType;
  id: string;
  tenant_id: string;
  name: string;
  description: string;
  color: string;
  icon: string;
  status: 'planning' | 'active' | 'on_hold' | 'completed' | 'archived';
  owner_id: string;
  repo_url?: string;
  entity_type_id?: string;
  entity_id?: string;
  start_date?: string;
  due_date?: string;
  created_at: string;
  updated_at: string;
  member_count: number;
  deleted_at?: string; // only on the deleted-projects list
}

export interface ProjectMember {
  id: string;
  project_id: string;
  user_id: string;
  role: 'owner' | 'admin' | 'member' | 'viewer';
  joined_at: string;
  email: string;
  first_name: string;
  last_name: string;
}

export interface Milestone {
  id: string;
  project_id: string;
  name: string;
  description: string;
  due_date?: string;
  status: 'upcoming' | 'in_progress' | 'completed' | 'overdue';
  created_at: string;
  // Populated by the project-scoped milestones list (LEFT JOIN tasks). Other
  // endpoints (e.g. POST /milestones returning a single milestone) don't
  // include them — treat absence as "unknown," not "zero."
  task_count?: number;
  done_count?: number;
}

export type TaskStatus = 'todo' | 'in_progress' | 'blocked' | 'review' | 'done';
export type TaskPriority = 'none' | 'low' | 'medium' | 'high' | 'urgent';

export interface Tag {
  id: string;
  project_id: string;
  name: string;
  color: string;
}

export interface Task {
  id: string;
  project_id: string;
  milestone_id?: string;
  parent_id?: string;
  follow_up_of?: string;
  tenant_id: string;
  title: string;
  description: string;
  status: TaskStatus;
  priority: TaskPriority;
  assignee_id?: string;
  assigned_by?: string;
  created_by: string;
  due_date?: string;
  start_date?: string;
  estimated_hours?: number;
  phase_id?: string;
  duration_days?: number;
  actual_hours?: number;
  actual_start?: string;
  actual_finish?: string;
  position: number;
  is_public: boolean;
  public_slug?: string;
  created_at: string;
  updated_at: string;
  tags: Tag[];
  subtask_count: number;
  deleted_at?: string; // only on the deleted-tasks list
}

export type CommentKind = 'human' | 'llm_reasoning' | 'llm_summary';

/** How a write was authenticated, recorded server-side from the verified
 *  credential (never client input). Absent = not recorded (older rows). */
export type AuthChannel = 'human' | 'api_key' | 'oauth_connector' | 'assistant';

/** 'unverified' until a trusted integration verifies checks/merge; none exists yet. */
export type EvidenceStatus = 'unverified';

export interface TaskComment {
  id: string;
  task_id: string;
  author_id: string;
  body: string;
  kind: CommentKind;
  /** Self-reported by the client: attribution text, never authority. */
  author_agent?: string;
  auth_channel?: AuthChannel;
  /** API key id (api_key) or OAuth client id (oauth_connector). */
  auth_client_id?: string;
  /** Superadmin behind an impersonation token (auth_channel is then 'human'). */
  auth_impersonated_by?: string;
  created_at: string;
  author_email: string;
  author_first_name: string;
  author_last_name: string;
}

export interface TaskCommit {
  id: string;
  task_id: string;
  sha: string;
  branch?: string;
  repo_url?: string;
  pr_url?: string;
  message?: string;
  author_id?: string;
  /** Self-reported by the client: attribution text, never authority. */
  author_agent?: string;
  auth_channel?: AuthChannel;
  auth_client_id?: string;
  /** Superadmin behind an impersonation token (auth_channel is then 'human'). */
  auth_impersonated_by?: string;
  created_at: string;
  evidence_status: EvidenceStatus;
}

export async function getTaskCommits(taskId: string): Promise<TaskCommit[]> {
  const { data } = await apiClient.get<{ data: TaskCommit[] }>(`/tasks/${taskId}/commits`);
  return data.data ?? [];
}

export interface TaskDependency {
  id: string;
  task_id: string;
  depends_on: string;
  dep_type: string;
  depends_on_title: string;
  /** false when the viewer may not see the prerequisite: depends_on is "" and
   *  depends_on_title is the generic "Restricted task". */
  accessible: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  /** backend/internal/pagination.Meta */
  meta: { total: number; page: number; limit: number; total_pages: number };
}

export async function getEntityTypes(): Promise<EntityType[]> {
  const { data } = await apiClient.get<{ data: EntityType[] }>('/entity-types');
  return data.data ?? [];
}

// ─── Users API ────────────────────────────────────────────────────────────────

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  is_superadmin?: boolean;
  // Skips the OTP step at sign-in. Changed only by a superadmin through
  // PATCH /auth/admin/users/{id}/otp-exempt.
  otp_exempt?: boolean;
  // Access-restriction flags. is_project_restricted limits the user to
  // projects they belong to; is_task_restricted limits them to tasks they're
  // assignee/creator of. Default false = full tenant visibility.
  is_project_restricted?: boolean;
  is_task_restricted?: boolean;
  tenant_id?: string;
  // Role NAMES (not IDs). Populated server-side from user_roles → roles join;
  // empty array when the user has no assigned roles. The admin Users page
  // displays these as badges in the Role column.
  roles?: string[];
}

export async function getUsers(limit = 100): Promise<User[]> {
  const { data } = await apiClient.get<{ data: User[] }>('/users', { params: { limit } });
  return data.data ?? [];
}

// Paginated variant used by admin surfaces that need total counts for
// prev/next UI. Flat getUsers() is kept for legacy callers (project
// detail, assignee pickers) that just want "give me everyone."
export async function getUsersPaginated(page: number, limit: number) {
  const { data } = await apiClient.get<PaginatedResponse<User>>('/users', { params: { page, limit } });
  return data;
}

export interface EffectivePermission {
  role: string;
  resource: string;
  action: string;
}

export async function getUserEffectivePermissions(userID: string): Promise<EffectivePermission[]> {
  const { data } = await apiClient.get<{ data: EffectivePermission[] }>(`/users/${userID}/permissions`);
  return data.data ?? [];
}

// ─── Projects API ─────────────────────────────────────────────────────────────

export async function getProjects(page = 1, limit = 50) {
  const { data } = await apiClient.get<PaginatedResponse<Project>>('/projects', { params: { page, limit } });
  return data;
}

export async function getEntityProjects(entityId: string): Promise<Project[]> {
  const { data } = await apiClient.get<{ data: Project[] }>(`/entities/${entityId}/projects`);
  return data.data ?? [];
}

export async function getProject(id: string) {
  const { data } = await apiClient.get<{ data: Project }>(`/projects/${id}`);
  return data.data;
}

// Project writes refresh every cached ['projects', ...] read (the sidebar shortcuts).
const projectsChanged = () => void queryClient.invalidateQueries({ queryKey: ['projects'] });

export async function createProject(input: Partial<Project>) {
  const { data } = await apiClient.post<{ data: Project }>('/projects', input);
  projectsChanged();
  return data.data;
}

export async function updateProject(id: string, input: Partial<Project>) {
  const { data } = await apiClient.put<{ data: Project }>(`/projects/${id}`, input);
  projectsChanged();
  return data.data;
}

export async function deleteProject(id: string) {
  await apiClient.delete(`/projects/${id}`);
  projectsChanged();
}

/** The tenant's soft-deleted projects, newest deletion first. */
export async function getDeletedProjects(page = 1, limit = 50) {
  const { data } = await apiClient.get<PaginatedResponse<Project>>('/projects/deleted', { params: { page, limit } });
  return data;
}

export async function restoreProject(id: string) {
  const { data } = await apiClient.post<{ data: Project }>(`/projects/${id}/restore`);
  projectsChanged();
  return data.data;
}

// ─── Project Members API ──────────────────────────────────────────────────────

export async function getProjectMembers(projectId: string) {
  const { data } = await apiClient.get<{ data: ProjectMember[] }>(`/projects/${projectId}/members`);
  return data.data ?? [];
}

export async function inviteProjectMember(projectId: string, email: string, role: string) {
  const { data } = await apiClient.post<{ data: unknown }>(`/projects/${projectId}/invite`, { email, role });
  return data.data;
}

/** Directly adds an existing user to a project by user_id (no email required). */
export async function addProjectMember(projectId: string, userId: string, role: string): Promise<ProjectMember> {
  const { data } = await apiClient.post<{ data: ProjectMember }>(`/projects/${projectId}/members`, { user_id: userId, role });
  return data.data;
}

export async function removeProjectMember(projectId: string, userId: string) {
  await apiClient.delete(`/projects/${projectId}/members/${userId}`);
}

export interface ProjectInvitation {
  id: string;
  project_id: string;
  email: string;
  role: string;
  expires_at: string;
  created_at: string;
  inviter_name?: string;
}

/** Pending (un-accepted) invitations for a project. */
export async function getProjectInvitations(projectId: string): Promise<ProjectInvitation[]> {
  const { data } = await apiClient.get<{ data: ProjectInvitation[] }>(`/projects/${projectId}/invitations`);
  return data.data ?? [];
}

export async function cancelProjectInvitation(projectId: string, invId: string) {
  await apiClient.delete(`/projects/${projectId}/invitations/${invId}`);
}

export async function resendProjectInvitation(projectId: string, invId: string): Promise<ProjectInvitation> {
  const { data } = await apiClient.post<ProjectInvitation>(`/projects/${projectId}/invitations/${invId}/resend`);
  return data;
}

export async function acceptInvitation(token: string): Promise<{ status: string; project_id: string }> {
  const { data } = await apiClient.post<{ data: { status: string; project_id: string } }>(`/invitations/${token}/accept`);
  return data.data;
}

// ─── Milestones API ───────────────────────────────────────────────────────────

export async function getMilestones(projectId: string) {
  const { data } = await apiClient.get<{ data: Milestone[] }>(`/projects/${projectId}/milestones`);
  return data.data ?? [];
}

export async function createMilestone(projectId: string, input: Partial<Milestone>) {
  const { data } = await apiClient.post<Milestone>(`/projects/${projectId}/milestones`, input);
  return data;
}

export async function updateMilestone(id: string, input: Partial<Milestone>) {
  const { data } = await apiClient.put<Milestone>(`/milestones/${id}`, input);
  return data;
}

export async function deleteMilestone(id: string) {
  await apiClient.delete(`/milestones/${id}`);
}

// ─── Tasks API ────────────────────────────────────────────────────────────────

export async function getTasks(projectId: string, params: Record<string, string> = {}) {
  const { data } = await apiClient.get<PaginatedResponse<Task>>(`/projects/${projectId}/tasks`, { params });
  return data;
}

export async function getTask(id: string) {
  const { data } = await apiClient.get<Task>(`/tasks/${id}`);
  return data;
}

export async function createTask(projectId: string, input: Partial<Task>) {
  const { data } = await apiClient.post<Task>(`/projects/${projectId}/tasks`, input);
  return data;
}

export async function updateTask(id: string, input: Partial<Task>) {
  const { data } = await apiClient.put<Task>(`/tasks/${id}`, input);
  return data;
}

export async function deleteTask(id: string) {
  await apiClient.delete(`/tasks/${id}`);
}

/** Soft-deleted tasks in a project — only those the caller may see (server-scoped). */
export async function getDeletedTasks(projectId: string, page = 1, limit = 50) {
  const { data } = await apiClient.get<PaginatedResponse<Task>>(`/projects/${projectId}/tasks/deleted`, { params: { page, limit } });
  return data;
}

/** Reverses a soft delete (POST /tasks/{id}/restore); returns the task. */
export async function restoreTask(id: string) {
  const { data } = await apiClient.post<Task>(`/tasks/${id}/restore`);
  return data;
}

export async function moveTask(id: string, status: TaskStatus, position: number) {
  const { data } = await apiClient.patch(`/tasks/${id}/move`, { status, position });
  return data;
}

export async function duplicateTask(id: string) {
  const { data } = await apiClient.post<Task>(`/tasks/${id}/duplicate`);
  return data;
}

export async function createFollowUpTask(id: string, input: Partial<Task>) {
  const { data } = await apiClient.post<Task>(`/tasks/${id}/follow-up`, input);
  return data;
}

export async function mergeTasks(sourceId: string, targetId: string) {
  const { data } = await apiClient.post(`/tasks/${sourceId}/merge`, { target_id: targetId });
  return data;
}

export async function setTaskVisibility(id: string, isPublic: boolean) {
  const { data } = await apiClient.patch<Task>(`/tasks/${id}/visibility`, { is_public: isPublic });
  return data;
}

// Subtasks
export async function getSubtasks(taskId: string) {
  const { data } = await apiClient.get<{ data: Task[] }>(`/tasks/${taskId}/subtasks`);
  return data.data ?? [];
}

export async function createSubtask(taskId: string, input: Partial<Task>) {
  const { data } = await apiClient.post<Task>(`/tasks/${taskId}/subtasks`, input);
  return data;
}

// Dependencies
export async function getDependencies(taskId: string) {
  const { data } = await apiClient.get<{ data: TaskDependency[] }>(`/tasks/${taskId}/dependencies`);
  return data.data ?? [];
}

export async function addDependency(taskId: string, dependsOnId: string, depType = 'finish_to_start') {
  const { data } = await apiClient.post<TaskDependency>(`/tasks/${taskId}/dependencies`, { depends_on_id: dependsOnId, dep_type: depType });
  return data;
}

export async function removeDependency(taskId: string, depId: string) {
  await apiClient.delete(`/tasks/${taskId}/dependencies/${depId}`);
}

// Comments
export async function getComments(taskId: string) {
  const { data } = await apiClient.get<{ data: TaskComment[] }>(`/tasks/${taskId}/comments`);
  return data.data ?? [];
}

export async function addComment(taskId: string, body: string) {
  const { data } = await apiClient.post<TaskComment>(`/tasks/${taskId}/comments`, { body });
  return data;
}

export async function deleteComment(taskId: string, commentId: string) {
  await apiClient.delete(`/tasks/${taskId}/comments/${commentId}`);
}

// Attachments
export interface TaskAttachment {
  id: string;
  task_id: string;
  uploaded_by?: string;
  uploader_name?: string;
  filename: string;
  file_size: number;
  content_type: string;
  created_at: string;
}

export async function getTaskAttachments(taskId: string): Promise<TaskAttachment[]> {
  const { data } = await apiClient.get<{ data: TaskAttachment[] }>(`/tasks/${taskId}/attachments`);
  return data.data ?? [];
}

export async function uploadTaskAttachment(taskId: string, file: File): Promise<TaskAttachment> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await apiClient.post<{ data: TaskAttachment }>(
    `/tasks/${taskId}/attachments`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data.data;
}

// Returns a short-lived presigned URL for the file. Pass { inline: true } to get
// a URL that renders in place (for image thumbnails); the default forces a download.
export async function getAttachmentDownloadUrl(
  taskId: string,
  attachmentId: string,
  opts?: { inline?: boolean },
): Promise<string> {
  const { data } = await apiClient.get<{ url: string }>(
    `/tasks/${taskId}/attachments/${attachmentId}/download`,
    opts?.inline ? { params: { disposition: 'inline' } } : undefined,
  );
  return data.url;
}

export async function deleteTaskAttachment(taskId: string, attachmentId: string): Promise<void> {
  await apiClient.delete(`/tasks/${taskId}/attachments/${attachmentId}`);
}

// Tags
export async function getTags(projectId: string) {
  const { data } = await apiClient.get<{ data: Tag[] }>(`/projects/${projectId}/tags`);
  return data.data ?? [];
}

export async function createTag(projectId: string, name: string, color: string) {
  const { data } = await apiClient.post<Tag>(`/projects/${projectId}/tags`, { name, color });
  return data;
}

export async function addTagToTask(taskId: string, tagId: string) {
  await apiClient.post(`/tasks/${taskId}/tags/${tagId}`);
}

export async function removeTagFromTask(taskId: string, tagId: string) {
  await apiClient.delete(`/tasks/${taskId}/tags/${tagId}`);
}

// ─── Analytics Types ──────────────────────────────────────────────────────────

export interface ProjectProgress {
  project_id: string;
  total_tasks: number;
  done_tasks: number;
  overdue_tasks: number;
  percent: number; // 0–100
}

export interface StatusCount {
  status: string;
  count: number;
}

export interface PortfolioProject {
  id: string;
  name: string;
  description: string;
  color: string;
  icon: string;
  status: string;
  due_date?: string;
  member_count: number;
  total_tasks: number;
  done_tasks: number;
  overdue_tasks: number;
  blocked_tasks: number;
  percent: number;
}

// ─── Analytics API ────────────────────────────────────────────────────────────

export async function getProjectProgress(projectId: string): Promise<ProjectProgress> {
  const { data } = await apiClient.get<{ data: ProjectProgress }>(`/projects/${projectId}/progress`);
  return data.data;
}

export async function getStatusBreakdown(projectId: string): Promise<StatusCount[]> {
  const { data } = await apiClient.get<{ data: StatusCount[] }>(`/projects/${projectId}/status-breakdown`);
  return data.data ?? [];
}

export async function getPortfolio(): Promise<PortfolioProject[]> {
  const { data } = await apiClient.get<{ data: PortfolioProject[] }>('/projects/portfolio');
  return data.data ?? [];
}
